/**
 * De camera's van dit appartement, en de vrijgave om er naar te kijken.
 *
 * Spec: specs/20260831-cameras-als-toestel/spec.md FR-22 t/m FR-26
 * Taak: T-3.12
 *
 * ── DE HOOK VRAAGT, DE SERVER BESLIST ───────────────────────────────────────
 *
 * Deze hook bevat GEEN kijkregel. Hij weet niet of er aangebeld wordt, telt geen 180
 * seconden af als grens en beslist niets. Hij vraagt `POST .../stream` en krijgt óf URL's
 * met een `expiresAt`, óf een 404. Dat is met opzet: een regel die in de app staat, is een
 * suggestie die een gewijzigde bundle of een stilstaande WebView negeert (spec §5.2).
 *
 * De aftelling die `expiresAt` mogelijk maakt is een VRIENDELIJKHEID — de bewoner ziet
 * waarom het beeld zo dadelijk stopt in plaats van dat het zonder uitleg zwart wordt.
 *
 * ── DE LIJST IS VOOR TE LADEN, HET BEELD NIET ───────────────────────────────
 *
 * `GET /resident/cameras` geeft alleen namen en volgorde en mag dus altijd; hij kost geen
 * beeld. Zo is er bij het aanbellen geen extra ronde nodig voordat de eerste tegel er
 * staat.
 *
 * ── CHROMIUM 84 IS DE ONDERGRENS ────────────────────────────────────────────
 *
 * De vloot draait gemengde firmware; `AbortSignal.timeout` (Chromium 103+) heeft hier
 * eerder twee probes stil laten falen. Er wordt daarom niets uit die hoek gebruikt: geen
 * `structuredClone`, geen `:has()`, en de axios-laag doet zijn eigen timeout.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import type { ResidentCamera, CameraStreamUrls } from '@caro/shared/types';

interface ListResponse {
  success: boolean;
  data: ResidentCamera[];
  schemaReady?: boolean;
}

interface StreamResponse {
  success: boolean;
  data: CameraStreamUrls;
}

export interface DoorbellCameras {
  /** De gekoppelde camera's in `sort_order`; de eerste is wat als eerste in beeld hoort. */
  cameras: ResidentCamera[];
  /** Vraagt een vrijgave. `null` = geweigerd (geen aanbelgebeurtenis, verlopen, of niet van deze unit). */
  requestStream: (cameraId: number) => Promise<CameraStreamUrls | null>;
}

export function useDoorbellCameras(enabled: boolean): DoorbellCameras {
  const { api, isAuthenticated } = useAuth();
  const [cameras, setCameras] = useState<ResidentCamera[]>([]);

  // Voorkomt dat een antwoord van een vorige oproep de state van de huidige overschrijft.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !isAuthenticated) return;
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.get<ListResponse>('/api/v1/resident/cameras');
        if (cancelled || !aliveRef.current) return;
        setCameras(Array.isArray(result?.data) ? result.data : []);
      } catch (err) {
        // Geen camera's is de NORMALE toestand voor verreweg de meeste appartementen, en
        // een mislukte lijst mag de deurbel niet in de weg zitten. Leeg is hier geen fout.
        if (!cancelled && aliveRef.current) setCameras([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [api, enabled, isAuthenticated]);

  const requestStream = useCallback(
    async (cameraId: number): Promise<CameraStreamUrls | null> => {
      try {
        const result = await api.post<StreamResponse>(
          `/api/v1/resident/cameras/${cameraId}/stream`,
          {},
        );
        if (!result || !result.data || !result.data.whepUrl) return null;
        return result.data;
      } catch (err) {
        // Een 404 is hier de VERWACHTE uitkomst zodra er niet (meer) aangebeld wordt. Hij
        // wordt daarom niet als fout getoond: de knop doet gewoon niets meer, en het beeld
        // valt terug op de videofoon.
        return null;
      }
    },
    [api],
  );

  return { cameras, requestStream };
}

/**
 * Seconden tot `expiresAt`, aflopend, of `null` als er geen vrijgave is.
 *
 * ⚠ Dit is een WEERGAVE en geen grens. Bereikt hij nul, dan stopt het beeld omdat de server
 * geen nieuwe vrijgave meer geeft — niet omdat deze teller iets afsluit. Wie de teller
 * wegneemt, verandert niets aan wanneer het beeld stopt; dat is precies de bedoeling.
 */
export function useGrantCountdown(expiresAt: string | null): number | null {
  const [seconds, setSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (!expiresAt) {
      setSeconds(null);
      return;
    }
    const end = new Date(expiresAt).getTime();
    const tick = () => {
      const left = Math.max(0, Math.round((end - Date.now()) / 1000));
      setSeconds(left);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  return seconds;
}
